import React, { memo } from 'react';
import type { WorkCard } from './workLayout';

interface WaterfallViewProps {
  /** 「加载更多」展开态：false 时底部渐变示意还有更多内容（裁剪窗口由 WorksSection 控制） */
  expanded: boolean;
  /** v1.4.108 已放置卡片（含实际渲染 x/y/w/h），由 WorksSection 实测列宽断点后统一布局计算 */
  cards: (WorkCard & { x: number; y: number; w: number; h: number })[];
  /** 画布总高（随列宽/用户作品动态变化；无作品 4 列 = 设计稿 1584 等比映射） */
  canvasH: number;
  /** 点击卡片 → 预览图片作品页（188:19），由 WorksSection → HomePage → AppNew 冒泡 */
  onOpen?: (card: WorkCard) => void;
}

/**
 * 图像作品瀑布流（设计稿 183:57 #250:4）——即梦式流式版
 * - 列数/列宽由 WorksSection 实测容器宽断点计算后布局，卡片直接以 x/y/w/h
 *   绝对定位渲染（无 1400 定宽画布、无 transform 缩放），窗口伸缩时实时重排
 * - 无用户作品 4 列时由 WorksSection 传入设计稿原始构图（等比映射）；其余情况瀑布流重排
 * - 悬停遮罩（#250:3）：底部渐变块（340 宽基准高 148，随列宽等比缩放），180° 渐变
 *   rgba(20,20,20) 0→0.4(35%)→0.8(100%)，无 blur；信息层（标题/头像24/用户名/点赞icon+数字）
 *   与渐变块同容器锚定卡片底部（left 12 / right 22 / bottom 16）；默认态裁剪窗口 1260
 *   会截掉底部卡片，故未展开时容器整体按「卡片底缘超出窗口量」上移，
 *   悬停信息恒落在渐变区内；展开（窗口=画布全高）后恢复贴卡片底
 * - 底部渐变（#183:182）：贴裁剪窗口底（1260 − 240 = top 1020；画布更矮时贴画布底），
 *   峰值 0.4（用户要求比设计稿 0.8 更透明），展开后隐藏
 */

const DEFAULT_AVATAR = '/images/home/avatars/avatar-user.png';

const WaterfallViewComponent: React.FC<WaterfallViewProps> = ({ expanded, cards, canvasH, onOpen }) => {
  return (
    /* 流式画布：高度即布局总高，卡片按实测坐标绝对定位，无缩放层 */
    <div className="relative w-full overflow-hidden" style={{ height: canvasH }}>
      {cards.map((card) => (
        <figure
          key={card.key}
          className="group absolute cursor-pointer overflow-hidden"
          style={{ left: card.x, top: card.y, width: card.w, height: card.h, borderRadius: 16 }}
          onClick={() => onOpen?.(card)}
        >
          <img
            src={card.src}
            alt={card.title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
          />

          {/* 悬停遮罩（#250:3）：底部渐变块 + 信息层同容器；未展开被裁时整体上移（bottom 公式见文件头） */}
          <div
            className="absolute inset-x-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            style={{
              bottom: expanded ? 0 : Math.max(0, card.y + card.h - 1260),
              height: Math.round((148 * card.w) / 340),
              background:
                'linear-gradient(180deg, rgba(20, 20, 20, 0) 0%, rgba(20, 20, 20, 0.4) 35%, rgba(20, 20, 20, 0.8) 100%)',
              borderRadius: 16,
            }}
          >
            {/* 信息层：left 12 / right 22 / bottom 16，与设计稿 (12,386)/(12,412)/(287,417) 对齐 */}
            <div className="absolute left-3 right-[22px]" style={{ bottom: 16 }}>
              <p className="truncate text-[14px] font-medium leading-[14px] text-white">{card.title}</p>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center">
                  <img
                    src={card.avatar || DEFAULT_AVATAR}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover"
                  />
                  <span className="ml-2 text-[14px] font-medium leading-[14px] text-white">{card.user}</span>
                </div>
                <div className="flex items-center">
                  <img src="/icons/home/icon-like.svg" alt="" className="h-6 w-6" />
                  <span className="ml-1 text-[14px] font-medium leading-[14px] text-white">{card.likes}</span>
                </div>
              </div>
            </div>
          </div>
        </figure>
      ))}

      {/* 底部渐变遮罩（#183:182）：相对裁剪窗口定位（1020 + 240 = 1260 窗口底），
          画布被压短时贴画布底；峰值 0.4（用户要求更透明），展开后隐藏 */}
      {!expanded && (
        <div
          className="pointer-events-none absolute left-0 w-full"
          style={{
            top: Math.min(1020, Math.max(0, canvasH - 240)),
            height: 240,
            background: 'linear-gradient(180deg, rgba(20, 20, 20, 0) 0%, rgba(20, 20, 20, 0.4) 100%)',
          }}
        />
      )}
    </div>
  );
};

export const WaterfallView = memo(WaterfallViewComponent);
